import type {
  ConversationClusteringRunLensType,
  ConversationClusteringRunOutputV1,
} from "@/types_api/backend/transport/get_latest_conversation_clustering_run_output_response"
import type { ConversationClusteringRunOutputV2 } from "@/types_api/backend/transport/get_latest_failure_pattern_run_output_response"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

export const CLUSTER_DESCRIPTION_COLUMN_KEY = "cluster_description"

/** Order the clustering run's lenses are shown in, first to last. */
export const RUN_LENS_DISPLAY_ORDER: Record<
  ConversationClusteringRunLensType | "failures",
  number
> = {
  call_topic: 0,
  personas: 1,
  anomalies: 2,
  failures: 3,
}

/**
 * Per-conversation descriptions from a clustering run, keyed by simulation
 * output id. A conversation sits in one cluster per lens and each lens carries
 * its own description; the first non-blank one in lens display order wins.
 */
export function buildClusterDescriptionsBySimulationOutputId(
  output: ConversationClusteringRunOutputV1 | ConversationClusteringRunOutputV2,
): Map<string, string> {
  const descriptions = new Map<string, string>()
  const lenses = output.agents
    .flatMap((agent) => agent.lenses)
    .filter((lens) => !("issues" in lens))
    .sort(
      (leftLens, rightLens) =>
        RUN_LENS_DISPLAY_ORDER[leftLens.lens_type ?? "anomalies"] -
        RUN_LENS_DISPLAY_ORDER[rightLens.lens_type ?? "anomalies"],
    )
  for (const lens of lenses) {
    if ("issues" in lens) {
      continue
    }
    for (const cluster of lens.clusters ?? []) {
      for (const simulationOutput of cluster.simulation_outputs) {
        const description = simulationOutput.description.trim()
        if (
          description &&
          !descriptions.has(simulationOutput.simulation_output_id)
        ) {
          descriptions.set(simulationOutput.simulation_output_id, description)
        }
      }
    }
  }
  return descriptions
}

/** Attaches each row's cluster description; rows without one are returned as-is. */
export function withClusterDescriptions(
  rows: ConversationsTableResult[],
  descriptions: Map<string, string>,
): ConversationsTableResult[] {
  if (descriptions.size === 0) {
    return rows
  }
  return rows.map((row) => {
    const description = row.simulation_output_id
      ? descriptions.get(row.simulation_output_id)
      : undefined
    return description ? { ...row, cluster_description: description } : row
  })
}
